import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuthRedirect } from '../utils/authRedirect';

const buildEditForm = (recipe) => ({
    title: recipe?.title || '',
    url: recipe?.url || '',
    ingredientsText: Array.isArray(recipe?.ingredients) ? recipe.ingredients.join('\n') : '',
    instructionsText: Array.isArray(recipe?.instructions) ? recipe.instructions.join('\n') : '',
    notesText: Array.isArray(recipe?.notes) ? recipe.notes.join('\n') : '',
});

const normalizeTextLines = (value) => String(value || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

function SavedRecipeDetail() {
    const { id } = useParams();
    const [recipe, setRecipe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [statusMessage, setStatusMessage] = useState('');
    const [newTag, setNewTag] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editForm, setEditForm] = useState(buildEditForm(null));
    const newTagRef = useRef(null);

    const handleAuthError = useAuthRedirect();

    useEffect(() => {
        const fetchRecipe = async () => {
            try {
                const token = localStorage.getItem('token');
                const recipeResponse = await fetch(`/api/recipes/${id}`, {
                    credentials: 'same-origin',
                    headers: {
                        'x-auth-token': token,
                    },
                });

                if (handleAuthError(recipeResponse.status)) {
                    return;
                }

                if (!recipeResponse.ok) {
                    const errData = await recipeResponse.json().catch(() => ({}));
                    throw new Error(errData.error || errData.msg || 'Something went wrong while fetching the recipe.');
                }

                const data = await recipeResponse.json();
                setRecipe(data);
                setEditForm(buildEditForm(data));
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchRecipe();
    }, [handleAuthError, id]);

    const handleAddTag = async () => {
        if (!recipe || newTag.trim() === '') return;

        try {
            setError('');
            const token = localStorage.getItem('token');
            const updatedTags = [...(Array.isArray(recipe.tags) ? recipe.tags : []), newTag.trim()];
            const response = await fetch(`/api/recipes/${id}`, {
                method: 'PUT',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'x-auth-token': token,
                },
                body: JSON.stringify({ tags: updatedTags }),
            });

            if (handleAuthError(response.status)) {
                return;
            }

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.error || errData.msg || 'Failed to add tag.');
            }

            const updatedRecipe = await response.json();
            setRecipe(updatedRecipe);
            setEditForm((prev) => ({ ...prev, ...buildEditForm(updatedRecipe) }));
            setNewTag('');
            if (newTagRef && newTagRef.current) newTagRef.current.focus();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleRemoveTag = async (tagToRemove) => {
        if (!recipe) return;
        try {
            setError('');
            const token = localStorage.getItem('token');
            const updatedTags = (Array.isArray(recipe.tags) ? recipe.tags : []).filter((tag) => tag !== tagToRemove);
            const response = await fetch(`/api/recipes/${id}`, {
                method: 'PUT',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'x-auth-token': token,
                },
                body: JSON.stringify({ tags: updatedTags }),
            });

            if (handleAuthError(response.status)) {
                return;
            }

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.error || errData.msg || 'Failed to remove tag.');
            }

            const updatedRecipe = await response.json();
            setRecipe(updatedRecipe);
            setEditForm((prev) => ({ ...prev, ...buildEditForm(updatedRecipe) }));
        } catch (err) {
            setError(err.message);
        }
    };

    const handleEditFieldChange = (field) => (event) => {
        setEditForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const handleStartEditing = () => {
        setStatusMessage('');
        setError('');
        setEditForm(buildEditForm(recipe));
        setIsEditing(true);
    };

    const handleCancelEditing = () => {
        setEditForm(buildEditForm(recipe));
        setIsEditing(false);
    };

    const handleSaveEdits = async () => {
        if (!recipe) return;

        const trimmedTitle = editForm.title.trim();
        if (!trimmedTitle) {
            setError('Recipe title is required.');
            return;
        }

        try {
            setIsSaving(true);
            setError('');
            setStatusMessage('');
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/recipes/${id}`, {
                method: 'PUT',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'x-auth-token': token,
                },
                body: JSON.stringify({
                    title: trimmedTitle,
                    url: editForm.url.trim(),
                    ingredients: normalizeTextLines(editForm.ingredientsText),
                    instructions: normalizeTextLines(editForm.instructionsText),
                    notes: normalizeTextLines(editForm.notesText),
                }),
            });

            if (handleAuthError(response.status)) {
                return;
            }

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.error || errData.msg || 'Failed to update recipe.');
            }

            const updatedRecipe = await response.json();
            setRecipe(updatedRecipe);
            setEditForm(buildEditForm(updatedRecipe));
            setIsEditing(false);
            setStatusMessage('Recipe updated.');
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const safeSourceUrl = (() => {
        if (!recipe || typeof recipe.url !== 'string') return '';
        try {
            const parsed = new URL(recipe.url);
            return ['http:', 'https:'].includes(parsed.protocol) ? parsed.toString() : '';
        } catch (err) {
            return '';
        }
    })();

    if (loading) {
        return (
            <div className="page saved-recipe-page">
                <section className="empty-state saved-recipe-state" aria-live="polite">
                    <h2>Loading recipe…</h2>
                    <p>Preparing your recipe details.</p>
                </section>
            </div>
        );
    }

    if (error && !recipe) {
        return (
            <div className="page saved-recipe-page">
                <section className="empty-state saved-recipe-state" role="alert">
                    <h2>Could not load recipe</h2>
                    <p>{error}</p>
                    <Link to="/saved-recipes" className="btn btn-secondary">Back to saved recipes</Link>
                </section>
            </div>
        );
    }

    if (!recipe) {
        return (
            <div className="page saved-recipe-page">
                <section className="empty-state saved-recipe-state">
                    <h2>Recipe not found</h2>
                    <p>This recipe may have been removed.</p>
                    <Link to="/saved-recipes" className="btn btn-secondary">Back to saved recipes</Link>
                </section>
            </div>
        );
    }

    const notes = Array.isArray(recipe.notes) ? recipe.notes : [];
    const ingredients = Array.isArray(recipe.ingredients) ? recipe.ingredients : [];
    const instructions = Array.isArray(recipe.instructions) ? recipe.instructions : [];
    const tags = Array.isArray(recipe.tags) ? recipe.tags : [];

    return (
        <div className="page saved-recipe-page">
            <header className="page-header">
                <span className="page-eyebrow">Saved recipe</span>
                <h1>{recipe.title}</h1>
                <p className="page-subtitle">
                    Keep this recipe tailored to your kitchen: edit ingredients, instructions, and personal notes anytime.
                </p>
            </header>

            {error && <div className="alert alert-danger" role="alert">{error}</div>}
            {statusMessage && <div className="alert alert-success" role="status">{statusMessage}</div>}

            <div className="saved-recipe-layout">
                <article className="card saved-recipe-card">
                    <div className="card-body">
                        <section className="saved-recipe-section">
                            <div className="saved-recipe-section__header">
                                <h3>Tags</h3>
                            </div>
                            <div className="saved-recipe-meta mb-3">
                                <div aria-live="polite" className="sr-only">{`You have ${tags.length} tags`}</div>
                                {tags.length === 0 ? (
                                    <span className="text-muted">No tags yet. Create your first tag below.</span>
                                ) : (
                                    tags.map((tag, index) => (
                                        <span key={index} className="tag-pill tag-pill--brand">
                                            {tag}
                                            <button
                                                type="button"
                                                aria-label={`Remove tag ${tag}`}
                                                disabled={isEditing}
                                                onClick={() => handleRemoveTag(tag)}
                                            >
                                                x
                                            </button>
                                        </span>
                                    ))
                                )}
                            </div>
                            <div className="tag-input-group">
                                <input
                                    ref={newTagRef}
                                    type="text"
                                    className="form-control"
                                    placeholder="Add new tag"
                                    value={newTag}
                                    disabled={isEditing}
                                    onChange={(e) => setNewTag(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddTag();
                                        }
                                    }}
                                />
                                <button className="btn btn-secondary" type="button" onClick={handleAddTag} disabled={isEditing}>
                                    Add tag
                                </button>
                            </div>
                            {isEditing && (
                                <p className="text-muted mb-0">Finish editing recipe fields before changing tags.</p>
                            )}
                        </section>

                        {isEditing ? (
                            <section className="saved-recipe-edit-grid" aria-label="Recipe editor">
                                <div className="saved-recipe-edit-field">
                                    <label className="form-label" htmlFor="edit-recipe-title">Title</label>
                                    <input
                                        id="edit-recipe-title"
                                        className="form-control"
                                        type="text"
                                        value={editForm.title}
                                        onChange={handleEditFieldChange('title')}
                                        placeholder="Recipe title"
                                    />
                                </div>
                                <div className="saved-recipe-edit-field">
                                    <label className="form-label" htmlFor="edit-recipe-source">Source URL</label>
                                    <input
                                        id="edit-recipe-source"
                                        className="form-control"
                                        type="text"
                                        value={editForm.url}
                                        onChange={handleEditFieldChange('url')}
                                        placeholder="https://example.com/recipe"
                                    />
                                </div>
                                <div className="saved-recipe-edit-field">
                                    <label className="form-label" htmlFor="edit-recipe-ingredients">Ingredients (one line each)</label>
                                    <textarea
                                        id="edit-recipe-ingredients"
                                        className="form-control"
                                        rows={8}
                                        value={editForm.ingredientsText}
                                        onChange={handleEditFieldChange('ingredientsText')}
                                    />
                                </div>
                                <div className="saved-recipe-edit-field">
                                    <label className="form-label" htmlFor="edit-recipe-instructions">Instructions (one line each)</label>
                                    <textarea
                                        id="edit-recipe-instructions"
                                        className="form-control"
                                        rows={8}
                                        value={editForm.instructionsText}
                                        onChange={handleEditFieldChange('instructionsText')}
                                    />
                                </div>
                                <div className="saved-recipe-edit-field saved-recipe-edit-field--full">
                                    <label className="form-label" htmlFor="edit-recipe-notes">Notes (one line each)</label>
                                    <textarea
                                        id="edit-recipe-notes"
                                        className="form-control"
                                        rows={5}
                                        value={editForm.notesText}
                                        onChange={handleEditFieldChange('notesText')}
                                        placeholder="e.g. Add lemon zest next time"
                                    />
                                </div>
                            </section>
                        ) : (
                            <>
                                <section className="saved-recipe-section">
                                    <div className="saved-recipe-section__header">
                                        <h3>Ingredients</h3>
                                    </div>
                                    {ingredients.length > 0 ? (
                                        <ul className="list-group">
                                            {ingredients.map((item, index) => (
                                                <li key={index} className="list-group-item">{item}</li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-muted mb-0">No ingredients saved yet.</p>
                                    )}
                                </section>

                                <section className="saved-recipe-section">
                                    <div className="saved-recipe-section__header">
                                        <h3>Instructions</h3>
                                    </div>
                                    {instructions.length > 0 ? (
                                        <ol className="list-group">
                                            {instructions.map((item, index) => (
                                                <li key={index} className="list-group-item">{item}</li>
                                            ))}
                                        </ol>
                                    ) : (
                                        <p className="text-muted mb-0">No instructions saved yet.</p>
                                    )}
                                </section>

                                <section className="saved-recipe-section">
                                    <div className="saved-recipe-section__header">
                                        <h3>Notes</h3>
                                    </div>
                                    {notes.length > 0 ? (
                                        <ul className="list-group">
                                            {notes.map((item, index) => (
                                                <li key={index} className="list-group-item">{item}</li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-muted mb-0">No notes yet. Add notes to remember tweaks and observations.</p>
                                    )}
                                </section>
                            </>
                        )}
                    </div>
                    <div className="card-footer text-center d-flex flex-wrap gap-2 justify-content-center">
                        {isEditing ? (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleCancelEditing}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSaveEdits}
                                    disabled={isSaving}
                                >
                                    {isSaving ? 'Saving changes…' : 'Save changes'}
                                </button>
                            </>
                        ) : (
                            <>
                                <button type="button" className="btn btn-secondary" onClick={handleStartEditing}>
                                    Edit recipe
                                </button>
                                <Link to="/cook-mode" state={{ recipe }} className="btn btn-primary">Start cook mode</Link>
                                <Link to="/saved-recipes" className="btn btn-outline-secondary">Back to saved recipes</Link>
                            </>
                        )}
                    </div>
                </article>

                <aside className="panel saved-recipe-aside">
                    <div className="saved-recipe-aside-block">
                        <h3>Source</h3>
                        {safeSourceUrl ? (
                            <a href={safeSourceUrl} target="_blank" rel="noopener noreferrer" className="saved-recipe-source-link">
                                Open original recipe
                            </a>
                        ) : (
                            <p className="text-muted mb-0">No source URL saved.</p>
                        )}
                    </div>
                    <div className="saved-recipe-aside-block">
                        <h3>At a glance</h3>
                        <ul className="list-group">
                            <li className="list-group-item">
                                <strong>{ingredients.length}</strong> ingredient{ingredients.length === 1 ? '' : 's'}
                            </li>
                            <li className="list-group-item">
                                <strong>{instructions.length}</strong> instruction{instructions.length === 1 ? '' : 's'}
                            </li>
                            <li className="list-group-item">
                                <strong>{notes.length}</strong> note{notes.length === 1 ? '' : 's'}
                            </li>
                        </ul>
                    </div>
                </aside>
            </div>
        </div>
    );
}

export default SavedRecipeDetail;
